import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '@/shared/lib';
import { Input } from '../input';

type TableSearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
};

export const TableSearchInput = ({
  value,
  onChange,
  placeholder = 'Search…',
  debounceMs = 300,
}: TableSearchInputProps) => {
  const [input, setInput] = useState(value);
  const debounced = useDebouncedValue(input, debounceMs);

  // Fire onChange from an effect (never during render) — calling a parent's
  // `navigate`/`setState` during render throws "Cannot update a component while
  // rendering a different component" and can loop until the value converges.
  // A ref keeps the latest callback without re-firing when its identity changes.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // `lastEmitted` tracks the value we last pushed upward, so we can tell an
  // out-of-band parent change (e.g. a Clear button setting value to '') from
  // the echo of our own onChange. Without it, syncing on every `value` change
  // would wipe the user's in-flight typing before the debounce fires.
  const lastEmitted = useRef(value);
  useEffect(() => {
    if (debounced !== lastEmitted.current) {
      lastEmitted.current = debounced;
      onChangeRef.current(debounced);
    }
  }, [debounced]);

  // Sync down only when the parent set `value` to something we didn't emit —
  // an external reset. Typing echoes back through `lastEmitted`, so this stays
  // a no-op during normal input.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setInput(value);
    }
  }, [value]);

  return (
    <Input
      leadingIcon={<Search style={{ height: 14, width: 14 }} />}
      onChange={(e) => setInput(e.target.value)}
      placeholder={placeholder}
      value={input}
    />
  );
};
